
import { CONST, InputDirection, InputState, getBlockSize } from "../const/const";

/**
 * Manages Keyboard and Touch input for the Tetris game.
 * Decouples input hardware events from game logic.
 */
export class InputManager {
    private scene: Phaser.Scene;
    private onInputCallback: (direction: InputDirection, state: InputState) => void;

    // Keys
    private keys: {
        LEFT: Phaser.Input.Keyboard.Key;
        RIGHT: Phaser.Input.Keyboard.Key;
        CTRL: Phaser.Input.Keyboard.Key;
        UP: Phaser.Input.Keyboard.Key;
        SPACE: Phaser.Input.Keyboard.Key;
        DOWN: Phaser.Input.Keyboard.Key;
        Z: Phaser.Input.Keyboard.Key;
        X: Phaser.Input.Keyboard.Key;
        C: Phaser.Input.Keyboard.Key;
        SHIFT: Phaser.Input.Keyboard.Key;
        ESC: Phaser.Input.Keyboard.Key;
    };
    public escKey: Phaser.Input.Keyboard.Key; // Public for Menu toggling

    // DAS State
    private dasFlags: Record<string, number> = {};

    // Touch State
    private touchStartX: number = 0;
    private touchStartY: number = 0;
    private touchStartTime: number = 0;
    private lastPointerX: number = 0;
    private lastPointerY: number = 0;
    private isTap: boolean = false;
    private pointerDownHandler: ((pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]) => void) | null = null;
    private pointerMoveHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null;
    private pointerUpHandler: ((pointer: Phaser.Input.Pointer) => void) | null = null;

    // Flags
    public isEnabled: boolean = true;
    private BLOCK_SIZE: number;

    // Sensitivity
    private DRAG_THRESHOLD_SCALE = 2;

    constructor(scene: Phaser.Scene, onInputCallback: (direction: InputDirection, state: InputState) => void) {
        this.scene = scene;
        this.onInputCallback = onInputCallback;
        this.BLOCK_SIZE = getBlockSize();

        this.setupKeys();
        this.setupTouchControls();
    }

    private setupKeys() {
        this.keys = {
            LEFT: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT),
            RIGHT: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT),
            CTRL: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.CTRL),
            UP: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.UP),
            SPACE: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE),
            DOWN: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN),
            Z: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.Z),
            X: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.X),
            C: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.C),
            SHIFT: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT),
            ESC: this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC)
        };
        this.escKey = this.keys.ESC;
    }

    private setupTouchControls() {
        this.scene.input.addPointer(1);
        this.scene.game.canvas.style.touchAction = 'none';

        this.pointerDownHandler = (pointer: Phaser.Input.Pointer, currentlyOver: Phaser.GameObjects.GameObject[]) => {
            if (!this.isEnabled) return;
            if (currentlyOver && currentlyOver.length > 0) return;

            this.touchStartX = pointer.worldX;
            this.touchStartY = pointer.worldY;
            this.touchStartTime = pointer.time;
            this.lastPointerX = pointer.worldX;
            this.lastPointerY = pointer.worldY;
            this.isTap = true;
        };
        this.scene.input.on('pointerdown', this.pointerDownHandler);

        this.pointerMoveHandler = (pointer: Phaser.Input.Pointer) => {
            if (!this.isEnabled || !pointer.isDown) return;

            const dist = Phaser.Math.Distance.Between(this.touchStartX, this.touchStartY, pointer.worldX, pointer.worldY);
            if (dist > 10) {
                this.isTap = false;
            }

            // Horizontal Move
            const deltaX = pointer.worldX - this.lastPointerX;
            if (Math.abs(deltaX) > (this.BLOCK_SIZE * this.DRAG_THRESHOLD_SCALE)) {
                if (deltaX > 0) {
                    this.emitInput('right', InputState.PRESS);
                    this.scene.time.delayedCall(50, () => this.emitInput('right', InputState.RELEASE));
                } else {
                    this.emitInput('left', InputState.PRESS);
                    this.scene.time.delayedCall(50, () => this.emitInput('left', InputState.RELEASE));
                }
                this.lastPointerX = pointer.worldX;
            }

            // Vertical Move (Soft Drop)
            const deltaY = pointer.worldY - this.lastPointerY;
            if (Math.abs(deltaY) > (this.BLOCK_SIZE * this.DRAG_THRESHOLD_SCALE) && !this.isTap) {
                if (deltaY > 0) {
                    this.emitInput('softDrop', InputState.PRESS);
                    this.scene.time.delayedCall(50, () => this.emitInput('softDrop', InputState.RELEASE));
                }
                this.lastPointerY = pointer.worldY;
            }
        };
        this.scene.input.on('pointermove', this.pointerMoveHandler);

        this.pointerUpHandler = (pointer: Phaser.Input.Pointer) => {
            if (!this.isEnabled) return;

            const duration = pointer.time - this.touchStartTime;
            const deltaY = pointer.worldY - this.touchStartY;

            // Release Soft Drop
            this.emitInput('softDrop', InputState.RELEASE);

            if (this.isTap) {
                // Check Rotation - use world-space center for correct comparison with pointer.worldX
                const cam = this.scene.cameras.main;
                const centerWidth = cam.scrollX + (cam.width / cam.zoom) / 2;

                if (pointer.worldX < centerWidth) {
                    this.emitInput('anticlockwise', InputState.PRESS);
                    this.scene.time.delayedCall(100, () => this.emitInput('anticlockwise', InputState.RELEASE));
                } else {
                    this.emitInput('clockwise', InputState.PRESS);
                    this.scene.time.delayedCall(100, () => this.emitInput('clockwise', InputState.RELEASE));
                }
            } else {
                // Check Hard Drop (Flick) - require primarily vertical swipe
                const deltaX = Math.abs(pointer.worldX - this.touchStartX);
                if (duration < 300 && deltaY > 50 && deltaX < 30 && deltaY > deltaX * 2) {
                    this.emitInput('hardDrop', InputState.PRESS);
                    this.scene.time.delayedCall(100, () => this.emitInput('hardDrop', InputState.RELEASE));
                }
            }
        };
        this.scene.input.on('pointerup', this.pointerUpHandler);
    }

    private emitInput(direction: InputDirection, state: InputState) {
        if (this.onInputCallback) {
            this.onInputCallback(direction, state);
        }
    }

    update(time: number, delta: number) {
        this.updateCustom(time, delta, CONST.PLAY_FIELD.DAS_MS, CONST.PLAY_FIELD.AR_MS);
    }

    /*
        https://tetris.wiki/DAS
    */
    private chargeDAS(input: InputDirection, isPressed: boolean, time: number, init?: number, repeat?: number) {
        if (!this.dasFlags[input]) this.dasFlags[input] = 0;
        const oldValue = this.dasFlags[input];

        if (isPressed) this.dasFlags[input] += time;
        else this.dasFlags[input] = 0;

        const newValue = this.dasFlags[input];

        if (oldValue == 0 && newValue) this.emitInput(input, InputState.PRESS);
        if (oldValue && newValue == 0) this.emitInput(input, InputState.RELEASE);

        if (newValue == 0) return;

        const initDelay = init || CONST.PLAY_FIELD.DAS_MS;
        const repeatDelay = repeat || CONST.PLAY_FIELD.AR_MS;

        let rOld = Math.floor((oldValue - initDelay) / repeatDelay);
        let rNew = Math.floor((newValue - initDelay) / repeatDelay);

        if (rNew >= 0 && rOld < rNew) {
            if (rOld < 0) rOld = -1;
            for (let i = 0; i < (rNew - rOld); ++i) {
                this.emitInput(input, InputState.HOLD);
            }
        }
    }

    public updateCustom(time: number, delta: number, softDropInit: number, softDropRepeat: number) {
        if (!this.isEnabled) return;

        this.chargeDAS("left", this.keys.LEFT.isDown, delta);
        this.chargeDAS("right", this.keys.RIGHT.isDown, delta);
        this.chargeDAS("softDrop", this.keys.DOWN.isDown, delta, softDropInit, softDropRepeat);
        this.chargeDAS("hardDrop", this.keys.SPACE.isDown, delta);
        this.chargeDAS("anticlockwise", this.keys.Z.isDown || this.keys.CTRL.isDown, delta);
        this.chargeDAS("clockwise", this.keys.X.isDown || this.keys.UP.isDown, delta);
        this.chargeDAS("hold", this.keys.C.isDown || this.keys.SHIFT.isDown, delta);
    }

    public setDragThresholdScale(scale: number) {
        this.DRAG_THRESHOLD_SCALE = scale;
    }

    public destroy() {
        if (this.pointerDownHandler) {
            this.scene.input.off('pointerdown', this.pointerDownHandler);
            this.pointerDownHandler = null;
        }
        if (this.pointerMoveHandler) {
            this.scene.input.off('pointermove', this.pointerMoveHandler);
            this.pointerMoveHandler = null;
        }
        if (this.pointerUpHandler) {
            this.scene.input.off('pointerup', this.pointerUpHandler);
            this.pointerUpHandler = null;
        }
    }
}
